/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BookingFilter } from 'types/app'
import { setFilter } from 'store/booking/BookingReducer'
import { Company } from 'types/entities/Company'
import { Timeslot } from 'types/entities/Timeslot'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { OfferBasicInputs } from 'components/Offers/schemas/useOfferBasicSchema'
import { UseFormReset, UseFormSetValue } from 'react-hook-form'
import { buildOfferBasicFormResetValues, calculateOfferValidUntil, hasBookingFilterChanges } from 'components/Offers/forms/OfferBasicFormUtils'
import { AppThunkDispatch } from 'helpers/store'

interface UseOfferBasicFormSyncParams {
    filter: BookingFilter
    dispatch: AppThunkDispatch
    defaultValues?: Partial<OfferBasicInputs>
    companyIdFromContext?: number | null
    selectedCompanyDefault?: Company | null
    useCompanyDefaultContact?: boolean
    setValue: UseFormSetValue<OfferBasicInputs>
    reset: UseFormReset<OfferBasicInputs>
}

export const useOfferBasicFormSync = ({
    filter,
    dispatch,
    defaultValues,
    companyIdFromContext,
    selectedCompanyDefault,
    useCompanyDefaultContact = false,
    setValue,
    reset,
}: UseOfferBasicFormSyncParams) => {
    const [selectedCompanyOverride, setSelectedCompanyOverride] = useState<Company | null | undefined>(undefined)
    const [selectedTimeslotLocal, setSelectedTimeslotLocal] = useState<Timeslot | null>(null)
    const latestFilterRef = useRef<BookingFilter>(filter)
    const companyDetailRequestIdRef = useRef<number | null>(null)

    useEffect(() => {
        latestFilterRef.current = filter
    }, [filter])

    const selectedCompany = useMemo(() => {
        if (selectedCompanyOverride !== undefined) {
            return selectedCompanyOverride
        }

        return selectedCompanyDefault ?? null
    }, [selectedCompanyDefault, selectedCompanyOverride])

    const setSelectedCompany = useCallback((company: Company | null) => {
        setSelectedCompanyOverride(company)
    }, [])

    useEffect(() => {
        if (!useCompanyDefaultContact) {
            return
        }

        const defaultContactId = selectedCompany?.default_contact_id
        setValue('contact_id', defaultContactId ? Number(defaultContactId) : undefined, {
            shouldValidate: Boolean(defaultContactId),
        })
    }, [selectedCompany?.default_contact_id, setValue, useCompanyDefaultContact])

    useEffect(() => {
        if (!selectedCompany) {
            companyDetailRequestIdRef.current = null
            return
        }

        const selectedCompanyId = Number(selectedCompany.id)
        if (
            !useCompanyDefaultContact ||
            selectedCompany.default_contact_id !== undefined ||
            companyDetailRequestIdRef.current === selectedCompanyId
        ) {
            return
        }

        let isCancelled = false
        companyDetailRequestIdRef.current = selectedCompanyId

        dispatch(CompaniesAction.fetchCompanyByIdRaw(selectedCompanyId))
            .then((company: Company) => {
                if (!isCancelled) {
                    setSelectedCompanyOverride(company)
                }
            })
            .catch((error: unknown) => {
                console.error('Failed to fetch company default contact:', error)
            })

        return () => {
            isCancelled = true
        }
    }, [dispatch, selectedCompany, useCompanyDefaultContact])

    const setDataToFilter = useCallback((data: Partial<BookingFilter>) => {
        const currentFilter = latestFilterRef.current

        if (!hasBookingFilterChanges(currentFilter, data)) {
            return
        }

        dispatch(
            setFilter({
                ...currentFilter,
                ...data,
            })
        )
    }, [dispatch])

    useEffect(() => {
        const values = buildOfferBasicFormResetValues({
            filter,
            defaultValues,
            companyIdFromContext,
        })

        reset(values)

        if (defaultValues) {
            setDataToFilter({
                adults: values.adults,
                children: values.children,
                date: values.date,
            })
        }
    }, [
        defaultValues?.date,
        defaultValues?.adults,
        defaultValues?.children,
        defaultValues?.contact_id,
        defaultValues?.probability,
        companyIdFromContext,
        reset,
    ])

    useEffect(() => {
        if (defaultValues?.adults === undefined && filter.adults !== undefined) {
            setValue('adults', filter.adults)
        }
        if (defaultValues?.children === undefined && filter.children !== undefined) {
            setValue('children', filter.children)
        }
        if (defaultValues?.date === undefined && filter.date) {
            setValue('date', filter.date)
            setValue('valid_until', calculateOfferValidUntil(filter.date))
        }
    }, [filter.adults, filter.children, filter.date, setValue, defaultValues])

    useEffect(() => {
        if (companyIdFromContext) {
            setValue('company_id', companyIdFromContext, {
                shouldValidate: false,
            })
        }
    }, [companyIdFromContext, setValue])

    useEffect(() => {
        if (selectedCompanyDefault && selectedCompanyOverride === undefined) {
            setValue('company_id', Number(selectedCompanyDefault.id), {
                shouldValidate: true,
            })
        }
    }, [selectedCompanyDefault, selectedCompanyOverride, setValue])

    useEffect(() => {
        if (companyIdFromContext && !selectedCompany && !selectedCompanyDefault) {
            dispatch(CompaniesAction.fetchCompanyById(companyIdFromContext))
                .then((company: Company) => {
                    setSelectedCompany(company)
                })
                .catch((error: unknown) => {
                    console.error('Failed to fetch company for autocomplete:', error)
                })
        }
    }, [companyIdFromContext, selectedCompany, selectedCompanyDefault, dispatch])

    return {
        selectedCompany,
        setSelectedCompany,
        selectedTimeslotLocal,
        setSelectedTimeslotLocal,
        setDataToFilter,
    }
}
